import fetchClient from '@/lib/fetch'

export interface SessionObjective {
  id: string
  title: string
  description: string | null
  mentorReportTemplate: string | null
  menteeReportTemplate: string | null
  expectedSessionCount: number
  order: number
  programId: string
  createdAt: string
  updatedAt: string
}

export interface SessionObjectiveWithProgress {
  id: string
  title: string
  description: string | null
  expectedSessionCount: number
  order: number
  completedSessionsCount: number
  linkedSessions: {
    id: string
    name: string | null
    from: string
  }[]
}

export interface CreateSessionObjectiveDTO {
  title: string
  description: string | null
  mentorReportTemplate: string | null
  menteeReportTemplate: string | null
  expectedSessionCount: number
  order: number
  programId: string
}

export interface UpdateSessionObjectiveDTO {
  title?: string
  description?: string | null
  mentorReportTemplate?: string | null
  menteeReportTemplate?: string | null
  expectedSessionCount?: number
  order?: number
}

export interface GetSessionObjectivesResponse {
  objectives: SessionObjective[]
}

export const SESSION_OBJECTIVES_QUERY_KEY = 'session-objectives'

/**
 * Get all session objectives for a program
 */
export async function getSessionObjectives(
  programId: string
): Promise<GetSessionObjectivesResponse> {
  return fetchClient(`/v2/session-objectives/program/${programId}`, {
    method: 'GET',
  })
}

/**
 * Get total expected sessions for a program
 */
export async function getTotalExpectedSessions(
  programId: string
): Promise<{ totalExpectedSessions: number }> {
  return fetchClient(`/v2/session-objectives/total-expected/${programId}`, {
    method: 'GET',
  })
}

/**
 * Create a new session objective
 */
export async function createSessionObjective(
  data: CreateSessionObjectiveDTO
): Promise<{ objective: SessionObjective }> {
  return fetchClient('/v2/session-objectives', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

/**
 * Update a session objective
 */
export async function updateSessionObjective(
  id: string,
  data: UpdateSessionObjectiveDTO
): Promise<{ objective: SessionObjective }> {
  return fetchClient(`/v2/session-objectives/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
}

/**
 * Delete a session objective
 */
export async function deleteSessionObjective(id: string): Promise<void> {
  return fetchClient(`/v2/session-objectives/${id}`, {
    method: 'DELETE',
  })
}

/**
 * Link a session to an objective
 */
export async function linkSessionToObjective(
  objectiveId: string,
  sessionId: string
): Promise<void> {
  return fetchClient(`/v2/session-objectives/${objectiveId}/link-session`, {
    method: 'POST',
    body: JSON.stringify({ sessionId }),
  })
}

/**
 * Unlink a session from an objective
 */
export async function unlinkSessionFromObjective(
  objectiveId: string,
  sessionId: string
): Promise<void> {
  return fetchClient(`/v2/session-objectives/${objectiveId}/unlink-session/${sessionId}`, {
    method: 'DELETE',
  })
}

/**
 * Get program objectives with progress calculated for a specific group (for dashboard)
 */
export async function getSessionObjectivesWithProgress(
  groupId: string
): Promise<{ objectives: SessionObjectiveWithProgress[]; hasMessages: boolean; conversationId: string }> {
  return fetchClient(`/v2/session-objectives/progress/group/${groupId}`, {
    method: 'GET',
  })
}
